import type { AxiosResponse } from "axios";

import { $streemApiV1 } from "shared/boot/api";
import { defaultMentionsFields, streamTypes } from "shared/constants";
import DateRange from "shared/helpers/DateRange";
import type { MediumName } from "shared/helpers/media";
import { getMediaForStream, getTypesForStream } from "shared/helpers/media";
import type { SortOptionField, SortOptionOrder } from "shared/helpers/mentions";
import serializeParamsForRequest from "shared/helpers/request";
import type {
  StreamRequestFilters,
  StreamRequestFiltersSortOption,
} from "shared/helpers/StreamFilters";
import useSyndicationStore from "shared/stores/syndication";
import type { Nullable, Stream } from "shared/types";
import type {
  Mention,
  SocialMentionType,
  SyndicationKey,
} from "shared/types/mentions";
import { MentionType } from "shared/types/mentions";

import {
  type FetchService,
  type FetchServiceEmptyResponse,
  type FetchServiceGetParams,
  type FetchServiceOptions,
} from "./FetchServiceFactory";

export interface FetchMentionsParams extends StreamRequestFilters {
  after: number;
  before: number;
  bundle_broadcast?: boolean;
  case_sensitive?: boolean;
  category_ids?: number[];
  collapse_syndicated?: boolean;
  document_ids?: SyndicationKey[];
  excerpt_radius: number;
  excluded_syndication_keys?: string[];
  fields?: string;
  filter_most_read_sources?: boolean;
  include_bookmark_syndication?: boolean;
  keyword?: string;
  limit: number;
  media?: MediumName[];
  page?: number;
  page_size?: number;
  query?: string;
  sort_by?: SortOptionField;
  sort_options?: StreamRequestFiltersSortOption[];
  sort_order?: SortOptionOrder;
  source_group_keys?: string[];
  source_keys?: string[];
  stream_ids?: number[];
  timestamps_in_milliseconds?: boolean;
  track_universal_search?: boolean;
  whole_text?: boolean;
}

interface FetchMentionsBuildOptions {
  params: FetchMentionsParams;
  signal?: AbortSignal;
  timeout?: number;
}

interface FetchMentionsAggregateOptions {
  collapseSyndicated?: boolean;
  field?: string;
  media?: MediumName[];
  search?: StreamRequestFilters;
  types?: MentionType[];
}

type FetchMentionsAggregateResponse = {
  doc_count: number;
  key: string;
  unique: {
    value: number;
  };
}[];

interface FetchMentionsCountParams {
  after: number;
  before: number;
  filterSetId?: Nullable<number>;
  search: StreamRequestFilters;
  stream: Pick<Stream, "id">;
}

interface FetchMentionsCountRequestParams {
  after: number;
  before: number;
  filterSetId?: number;
  limit: number;
  search?: StreamRequestFilters;
  syndicationKeys?: string[];
}

interface FetchMentionsBuildParams {
  media: MediumName[];
  mentions: Mention[];
  options: FetchServiceOptions;
  stream: Stream;
}

export interface FetchMentions extends FetchService {
  aggregate: (
    stream: Stream,
    options: FetchMentionsAggregateOptions,
    range: DateRange
  ) => Promise<FetchMentionsAggregateResponse>;
  buildOptions(params: FetchMentionsBuildParams): FetchMentionsBuildOptions;
  mentionCount(params: FetchMentionsCountParams): Promise<AxiosResponse>;
  parseQuery(query: string): string;
  streamMedia(stream: Stream, options: FetchServiceOptions): MediumName[];
}

export const fetchMentions: FetchMentions = {
  aggregate(
    stream: Stream,
    options: FetchMentionsAggregateOptions,
    range: DateRange
  ): Promise<FetchMentionsAggregateResponse> {
    const params = {
      after: range.after,
      before: range.before,
      ...(options.collapseSyndicated ? { collapse_syndicated: true } : {}),
      ...(options.media ? { media: options.media } : {}),
      ...(options.field ? { field: options.field } : {}),
      types: getTypesForStream(stream),
      ...(options.types ? { types: options.types } : {}),
      ...(options.search ? options.search : {}),
    };

    const serializedParams = serializeParamsForRequest(params);

    return $streemApiV1
      .get(`streams/${stream.id}/aggregate?${serializedParams}`)
      .then((response) => response.data);
  },

  buildOptions({
    media,
    mentions,
    options = { range: DateRange.lastYear() },
    stream,
  }: FetchMentionsBuildParams): FetchMentionsBuildOptions {
    let { after, before } = options.range;

    let timestampProp: keyof Mention = "timestamp";

    if (options.useMilliseconds) {
      timestampProp = "timestamp_milliseconds";
      after *= 1000;
      before *= 1000;
    }

    const latestMention: Mention | undefined = options.loadLatestMentions
      ? mentions.at(0)
      : mentions.at(-1);

    if (options.keepAlreadyLoadedMentions && latestMention && !options.page) {
      before = latestMention[timestampProp];
    }

    let types: (MentionType | SocialMentionType)[] = getTypesForStream(stream);

    if (options.types) {
      types = options.types;
    }

    const fields = options.fields || defaultMentionsFields;

    const includesBroadcastStory =
      types.includes(MentionType.radio_story) ||
      types.includes(MentionType.tv_story);

    const query =
      options.endpoint === "search/journalists"
        ? stream.keywords
        : this.parseQuery(stream.keywords || "");

    return {
      ...(options.timeout ? { timeout: options.timeout } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      params: {
        after,
        before,
        excerpt_radius: options.excerpt_radius || 60,
        limit: options.limit ? options.limit : 10,
        whole_text: true,
        ...(options.allFields || !stream.id
          ? {}
          : {
              fields: includesBroadcastStory ? `${fields},fragments` : fields,
            }),
        ...(options.collapse_syndicated ? { collapse_syndicated: true } : {}),
        ...(options.bundle_broadcast ? { bundle_broadcast: true } : {}),
        ...(options.documentIds?.length
          ? { document_ids: options.documentIds }
          : {}),
        ...(options.sort_options ? { sort_options: options.sort_options } : {}),
        ...(options.sortBy ? { sort_by: options.sortBy } : {}),
        ...(options.sortOrder ? { sort_order: options.sortOrder } : {}),
        ...(options.pageSize ? { page_size: options.pageSize } : {}),
        ...(options.page ? { page: options.page } : {}),
        ...(media?.length &&
        ![streamTypes.socialStream, streamTypes.bookmarkStream].includes(
          stream.type
        )
          ? { media }
          : {}),
        ...(options.case_sensitive !== undefined
          ? { case_sensitive: options.case_sensitive }
          : {}),
        ...(options.syndicationKeys
          ? { excluded_syndication_keys: options.syndicationKeys }
          : {}),
        ...(options.useMilliseconds
          ? { timestamps_in_milliseconds: options.useMilliseconds }
          : {}),
        ...(types?.length ? { types } : {}),
        ...(options.includeBookmarkSyndication !== undefined
          ? { include_bookmark_syndication: options.includeBookmarkSyndication }
          : {}),
        ...(options.stream_ids && { stream_ids: options.stream_ids }),
        ...(options.shouldTrackUniversalSearch && {
          track_universal_search: true,
        }),
        ...(stream.filters && {
          category_ids: stream.filters.map(({ filter }) => filter.id),
        }),
        ...(options.source_keys && { source_keys: options.source_keys }),
        ...(options.source_group_keys && {
          source_group_keys: options.source_group_keys,
        }),
        ...(options.keyword && { keyword: options.keyword }),
        ...(options.search ? options.search : {}),
        ...(query ? { query } : {}),
        ...(options.filterSetId ? { filter_set_id: options.filterSetId } : {}),
      },
    };
  },

  get({
    mentions,
    options = { range: DateRange.lastYear() },
    stream,
  }: FetchServiceGetParams): Promise<
    AxiosResponse<Mention[]> | FetchServiceEmptyResponse
  > {
    const media = fetchMentions.streamMedia(stream, options);

    const { skipMediaValidation } = options;

    if (
      !media.length &&
      !getTypesForStream(stream).length &&
      !skipMediaValidation
    ) {
      return Promise.resolve({
        data: [],
        headers: { "x-total-count": 0, "x-total-pages": 0 },
      });
    }

    let endpoint = "search";

    if (options.endpoint) {
      endpoint = options.endpoint;
    } else if (stream.id) {
      endpoint = `streams/${stream.id}/mentions`;
    }

    const params = fetchMentions.buildOptions({
      media,
      mentions,
      options: {
        ...options,
        ...(!stream.id ? { excerpt_radius: 150 } : {}),
        ...(options.filterSetId ? { filter_set_id: options.filterSetId } : {}),
      },
      stream,
    });

    return $streemApiV1.post(endpoint, params);
  },

  getIds({
    mentions,
    options,
    stream,
  }: FetchServiceGetParams): Promise<
    | AxiosResponse<SyndicationKey[]>
    | FetchServiceEmptyResponse<SyndicationKey[]>
  > {
    const media = fetchMentions.streamMedia(stream, options);

    const { skipMediaValidation } = options;

    if (
      !media.length &&
      !getTypesForStream(stream).length &&
      !skipMediaValidation
    ) {
      return Promise.resolve({
        data: [],
        headers: { "x-total-count": 0, "x-total-pages": 0 },
      });
    }

    const requestOptions = fetchMentions.buildOptions({
      media,
      mentions,
      options,
      stream,
    });

    return $streemApiV1.post(
      `streams/${stream.id}/mention_ids`,
      requestOptions
    );
  },

  mentionCount({
    after,
    before,
    filterSetId,
    search,
    stream,
  }: FetchMentionsCountParams): Promise<AxiosResponse> {
    const { streamSyndicationKeys } = useSyndicationStore() as unknown as {
      streamSyndicationKeys: {
        [key: number]: string[];
      };
    };

    const syndicationKeys = streamSyndicationKeys[stream.id] || [];

    const requestOptions: FetchMentionsCountRequestParams = {
      after,
      before,
      limit: 0,
      syndicationKeys,
      ...search,
      ...(filterSetId ? { filter_set_id: filterSetId } : {}),
    };

    return $streemApiV1.post(`streams/${stream.id}/mention_count`, {
      handleErrors: false,
      params: requestOptions,
    });
  },

  parseQuery(query: string): string {
    const regexp = "(AND NOT|AND|OR|NOT|UNLESS|NEAR/|ONEAR/|FIRST/|ATLEAST/)";

    if (query.match(new RegExp(regexp))) {
      return query;
    }

    const keywords: string[] = [];

    query
      .replace(/"/g, "")
      .split(/,(?!(\d))/)
      .forEach((word) => {
        if (word) keywords.push(`"${word.trim()}"`);
      });

    return keywords.join(",");
  },

  streamMedia(stream: Stream, options: FetchServiceOptions) {
    let media = getMediaForStream(stream);

    const { excludedMedia } = options;

    if (excludedMedia?.length) {
      media = media.filter((medium) => !excludedMedia.includes(medium));
    }

    return media;
  },
};
